import React, { useEffect } from "react";
import Accordion from "../../Accordian/Accordian";
import style from "../Information/style.module.css";
import {
  getAccessTypesFromPublishing,
  nextPlacementKey,
  placementPairKey,
  showsPlacementSection,
  syncAccessFields,
  toPlacementId,
} from "../Information/PublishingPayload";

const STATUS_OPTIONS = ["Publish", "Save Draft"];
const CONTENT_TIER_OPTIONS = ["Free", "Premium", "PPV"];
const FEATURED_PLACEMENT_OPTIONS = ["Not Featured", "Homepage", "Category Page", "Banner"];
const ACCESS_TIER_OPTIONS = ["Free", "Paid"];

const getHash = () => {
  try {
    return typeof window !== "undefined" ? window.location.hash : "";
  } catch {
    return "";
  }
};

const CURRENCY_OPTIONS = ["USD", "EUR", "GBP"];

const ACCESS_OPTIONS = [
  { value: "subscription", label: "Subscription" },
  { value: "buy", label: "Buy" },
  { value: "rent", label: "Rent" },
];

const clearRentFields = (next) => {
  next.rent_price = "";
  next.rent_duration_days = "";
  return next;
};

const clearBuyFields = (next) => {
  next.buy_price = "";
  return next;
};


// lockAccess: access is set by a parent (a season follows its series), so every
// access control is read-only; the caller keeps publishingData in sync.
// accessNote: optional hint shown next to the Access label.
const Publishing = ({
  soloProps,
  publishingData,
  setPublishingData,
  isSeries,
  lockAccess = false,
  accessNote,
  showPlacementErrors = false,
  // Experience Builder pages come from the parent form (useCategoryPlacements)
  // rather than being fetched here: this accordion's body unmounts while
  // collapsed, so anything fetched here would be missing for a user who never
  // opened it -- including the rows the save is about to require.
  sections = [],
  sectionsError = false,
  sectionsLoading = false,
  // Series only: "Micro Drama" shows the "Free episodes" field.
  seriesFormat = "",
}) => {
  const hash = getHash();

  const isField =
    hash.includes("/Addseries") || hash.includes("/add");

  const Episodes =
    hash.includes("/episodes");

  const isEpisodeAdd = hash.split("/").length >= 5 && hash.includes("/video/");

  // Shared with the parent forms' save-time validation so the two can't drift.
  // TWO DIFFERENT QUESTIONS, deliberately not one gate:
  //  - showPlacementRows: which forms show the Category / Sub Category rows.
  //    showsPlacementSection() also excludes /seasons/add.
  //  - showAccessControls: which forms show the Access tier + access types.
  //    Seasons DO show these (read-only, via lockAccess) -- that is the whole
  //    point of the prop, so this must NOT use the placement gate.
  const showPlacementRows = showsPlacementSection(hash);
  const showAccessControls = !isEpisodeAdd && !Episodes;

  const accessTypes = getAccessTypesFromPublishing(publishingData);
  const hasSubscription = accessTypes.includes("subscription");
  const hasBuy = accessTypes.includes("buy");
  const hasRent = accessTypes.includes("rent");
  const showRentFields = hasRent;
  const showBuyFields = hasBuy;
  const showPaidPricing = showRentFields || showBuyFields;

  const handleAccessToggle = (mode) => {
    setPublishingData((prev) => {
      let types = [...getAccessTypesFromPublishing(prev)];

      if (mode === "subscription") {
        types = types.includes("subscription") ? [] : ["subscription"];
      } else {
        types = types.filter((t) => t !== "subscription");
        if (types.includes(mode)) {
          types = types.filter((t) => t !== mode);
        } else {
          types.push(mode);
        }
      }

      const next = {
        ...prev,
        ...syncAccessFields(types),
        access: prev.access,
      };

      if (!types.includes("rent")) clearRentFields(next);
      if (!types.includes("buy")) clearBuyFields(next);
      if (!types.includes("buy") && !types.includes("rent")) next.currency = "";

      return next;
    });
  };

  const accessTier = publishingData?.access === "Paid" ? "Paid" : publishingData?.access === "Free" ? "Free" : "";
  const isPaid = accessTier === "Paid";
  useEffect(() => {
    if (!publishingData) return;

    setPublishingData((prev) => {
      if (!prev) return prev;
      const needsStatus = !prev.status;
      const needsAccess = !prev.access;

      if (!needsStatus && !needsAccess) return prev;

      return {
        ...prev,
        ...(needsStatus ? { status: "Publish" } : {}),
        ...(needsAccess ? { access: "Free" } : {}),
      };
    });
  }, []);

  const categoryPlacements = Array.isArray(publishingData?.category_placements)
    ? publishingData.category_placements
    : [];

  // The parent form seeds the first row (useCategoryPlacements) so it exists
  // even when this accordion has never been opened. This is the safety net for
  // the case where an edit load replaces state after that ran: without a row
  // there is nothing to type into, only "+ Add Category".
  //
  // Placements are optional, so this blank row is purely an affordance -- it
  // shows the feature exists. An empty row is ignored on save.
  useEffect(() => {
    if (!showPlacementRows || categoryPlacements.length) return;
    setPublishingData((prev) => {
      if (!prev) return prev;
      const rows = Array.isArray(prev.category_placements) ? prev.category_placements : [];
      if (rows.length) return prev;
      return {
        ...prev,
        category_placements: [{ _key: nextPlacementKey(), section_id: "", category_id: "" }],
      };
    });
  // setPublishingData is a stable setState; publishingData is read only to
  // check emptiness, which categoryPlacements.length already tracks.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPlacementRows, categoryPlacements.length]);

  // Pair -> the row that holds it, so a row never greys out its own current
  // selection while still blocking a pair another row already uses.
  const pairOwners = new Map();
  categoryPlacements.forEach((p) => {
    if (!p.section_id || !p.category_id) return;
    const key = placementPairKey(p);
    if (!pairOwners.has(key)) pairOwners.set(key, p._key);
  });

  const hasIncompletePlacement = categoryPlacements.some((p) => !p.section_id || !p.category_id);

  // A new row is only useful once the existing ones are filled in -- otherwise
  // "+ Add Category" just stacks up blank rows that fail validation on save.
  const handleAddPlacement = () => {
    setPublishingData((prev) => {
      const rows = Array.isArray(prev?.category_placements) ? prev.category_placements : [];
      if (rows.some((p) => !p.section_id || !p.category_id)) return prev;
      return {
        ...prev,
        category_placements: [
          ...rows,
          { _key: nextPlacementKey(), section_id: "", category_id: "" },
        ],
      };
    });
  };

  const handleRemovePlacement = (key) => {
    setPublishingData((prev) => ({
      ...prev,
      category_placements: (prev?.category_placements || []).filter((p) => p._key !== key),
    }));
  };

  // Changing the page (Category) invalidates whatever Sub Category was
  // picked before -- it belonged to the previous page's rails.
  const handlePlacementSectionChange = (key, value) => {
    setPublishingData((prev) => ({
      ...prev,
      category_placements: (prev?.category_placements || []).map((p) =>
        p._key === key ? { ...p, section_id: value, category_id: "" } : p
      ),
    }));
  };

  const handlePlacementCategoryChange = (key, value) => {
    setPublishingData((prev) => ({
      ...prev,
      category_placements: (prev?.category_placements || []).map((p) =>
        p._key === key ? { ...p, category_id: value } : p
      ),
    }));
  };

  // Access is just Free or Paid -- there is no Access Type (subscription /
  // buy / rent) or TVOD pricing any more, so any legacy values are cleared.
  const handleAccessTierChange = (e) => {
    const value = e.target.value;
    setPublishingData((prev) => ({
      ...prev,
      access: value === "Free" || value === "Paid" ? value : "",
      access_type: "",
      access_types: [],
      rent_price: "",
      rent_duration_days: "",
      buy_price: "",
      currency: "",
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPublishingData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <Accordion
      title="Publishing"
      icon={<img src={`${process.env.PUBLIC_URL}/Publishing.png`} alt="" width={18} height={18} />}
      openId="publishing"
      {...soloProps}
    >
      <div className={style.publishingWrapper}>

        {/* ── Row 1: Status + Access Tier ── */}
        <div className={style.publishingRow}>
          <div className={style.publishingGroup}>
            <label className={style.publishingLabel}>
              Status <span style={{ color: "var(--app-primary)" }}>*</span>
            </label>
            <select
              name="status"
              className={style.publishingSelect}
              value={publishingData?.status || ""}
              onChange={handleChange}
            >
              <option value="">Select</option>
              {STATUS_OPTIONS?.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          </div>

          {showAccessControls && (
            <div className={style.publishingGroup}>
              <label className={style.publishingLabel}>
                Access <span style={{ color: "var(--app-primary)" }}>*</span>
                {lockAccess && <span className={style.publishingLabelNote}>Set by the series</span>}
                {accessNote && <span className={style.publishingLabelNote}>{accessNote}</span>}
              </label>
              <select
                name="Access"
                className={style.publishingSelect}
                value={accessTier}
                onChange={handleAccessTierChange}
                disabled={lockAccess}
              >
                <option value="">Select</option>
                {ACCESS_TIER_OPTIONS.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>
          )}

          {isSeries && seriesFormat === "Micro Drama" && (
            <div className={style.publishingGroup}>
              <label className={style.publishingLabel} htmlFor="free_episodes">
                Free episodes
              </label>
              <input
                id="free_episodes"
                name="free_episodes"
                type="number"
                min="0"
                step="1"
                inputMode="numeric"
                className={style.publishingSelect}
                placeholder="e.g. 5"
                value={publishingData?.free_episodes ?? ""}
                onChange={handleChange}
                aria-describedby="free_episodes_hint"
              />
              <span id="free_episodes_hint" style={{ fontSize: 12, opacity: 0.75, marginTop: 4 }}>
                Episodes 1–N of Season 1 play free; the rest follow Access.
              </span>
            </div>
          )}
        </div>

        {/* ── Row 1b: Category (Experience Builder page) + Sub Category
             (the specific rail/shelf within that page) -- repeatable, one
             content item can be placed on several pages/rails. Assigned by
             pushing this content's id into each SectionCategory.contents on
             save (see applyCategoryPlacements in Backend). ── */}
        {showPlacementRows && (
          <div className={style.publishingRow} style={{ flexDirection: "column", gap: 12 }}>
            {categoryPlacements.map((placement) => {
              const placementSection = sections.find(
                (section) => String(section.id) === String(placement.section_id)
              );
              const placementSubCategories = placementSection?.categories || [];
              // A saved placement whose page is no longer in the list (deleted,
              // beyond the fetch limit, or the request failed). Without this the
              // row renders as a plain "Select" -- indistinguishable from never
              // having been set -- while still silently re-saving the stale id.
              const orphanedSection =
                Boolean(placement.section_id) &&
                !placementSection &&
                !sectionsError &&
                !sectionsLoading;
              // A Sub Category already paired with this Category in another row
              // would be a duplicate placement, so it can't be picked again.
              // The row's own current pick stays selectable.
              const isTakenHere = (categoryId) => {
                const owner = pairOwners.get(
                  placementPairKey({ section_id: placement.section_id, category_id: categoryId })
                );
                return Boolean(owner) && owner !== placement._key;
              };
              // Every Sub Category of this Category is already used elsewhere.
              // Only meaningful for a row that still has to pick one -- a row
              // that already holds a value isn't stuck.
              const sectionExhausted =
                !placement.category_id &&
                placementSubCategories.length > 0 &&
                placementSubCategories.every((category) => isTakenHere(category.id));
              const subCategoryMissing =
                showPlacementErrors && Boolean(placement.section_id) && !placement.category_id;

              return (
                <div key={placement._key} style={{ width: "100%" }}>
                  <div style={{ display: "flex", gap: 16, alignItems: "flex-end", width: "100%" }}>
                    <div className={style.publishingGroup} style={{ flex: 1 }}>
                      <label className={style.publishingLabel}>
                        Category
                      </label>
                      <select
                        className={style.publishingSelect}
                        value={toPlacementId(placement.section_id)}
                        onChange={(e) => handlePlacementSectionChange(placement._key, e.target.value)}
                      >
                        <option value="">{sectionsLoading ? "Loading..." : "Select"}</option>
                        {sections.map((section) => (
                          <option key={section.id} value={section.id}>{section.name}</option>
                        ))}
                      </select>
                    </div>

                    <div className={style.publishingGroup} style={{ flex: 1 }}>
                      <label className={style.publishingLabel}>
                        {/* Placements are optional, so no asterisk by default --
                            but once a Category is picked on this row, its Sub
                            Category has to be filled in or the row is dropped. */}
                        Sub Category {Boolean(placement.section_id) && (
                          <span style={{ color: "var(--app-primary)" }}>*</span>
                        )}
                      </label>
                      <select
                        className={style.publishingSelect}
                        value={toPlacementId(placement.category_id)}
                        onChange={(e) => handlePlacementCategoryChange(placement._key, e.target.value)}
                        disabled={!placement.section_id || orphanedSection}
                      >
                        <option value="">Select</option>
                        {placementSubCategories.map((category) => {
                          // Never disable the value this row currently holds --
                          // a duplicate loaded from legacy data would otherwise
                          // render its own selection greyed out and unexplained.
                          // The save-time check still rejects the duplicate.
                          const isOwnValue =
                            toPlacementId(category.id) === toPlacementId(placement.category_id);
                          const taken = !isOwnValue && isTakenHere(category.id);
                          return (
                            <option key={category.id} value={category.id} disabled={taken}>
                              {category.name}{taken ? " (already added)" : ""}
                            </option>
                          );
                        })}
                      </select>
                    </div>

                    {/* The first row stays put -- one placement is mandatory, so
                        removing it would only force the empty row back. */}
                    {categoryPlacements.length > 1 ? (
                      <button
                        type="button"
                        className={style.trailerRemoveBtn}
                        onClick={() => handleRemovePlacement(placement._key)}
                        aria-label="Remove category"
                      >
                        &times;
                      </button>
                    ) : (
                      /* Spacer keeping the single row aligned with multi-row
                         layouts. A plain element, not a hidden <button>, so
                         there is no focusable control in the tree. */
                      <span className={style.placementRemoveSpacer} aria-hidden="true" />
                    )}
                  </div>

                  {/* Most actionable message wins: a page that's gone, then a
                      page with nothing left to pick, then the plain "pick one".
                      Telling someone to choose a Sub Category from a list where
                      every option is disabled is a dead end. */}
                  {orphanedSection ? (
                    <span className={style.publishingErrorText} role="alert">
                      This Category is no longer available. Please pick another one.
                    </span>
                  ) : sectionExhausted ? (
                    <span className={style.publishingErrorText} role="alert">
                      Every Sub Category of this Category is already added.
                    </span>
                  ) : subCategoryMissing ? (
                    <span className={style.publishingErrorText} role="alert">
                      Please select a Sub Category for this Category.
                    </span>
                  ) : null}
                </div>
              );
            })}

            {sectionsError && (
              <span className={style.publishingErrorText} role="alert">
                Couldn't load the Category list. Please reload the page and try again.
              </span>
            )}

            {/* Disabled rather than silently doing nothing: the click used to
                no-op while a row was incomplete, which reads as a broken button. */}
            <button
              type="button"
              className={style.addTrailerBtn}
              onClick={handleAddPlacement}
              disabled={hasIncompletePlacement}
              title={
                hasIncompletePlacement
                  ? "Finish the current Category and Sub Category first"
                  : undefined
              }
              style={hasIncompletePlacement ? { opacity: 0.6, cursor: "not-allowed" } : undefined}
            >
              + Add Category
            </button>
          </div>
        )}

        {/* ── Row 2: Access Type checkboxes — only when Paid ── */}
        {showAccessControls && isPaid && (
          <div className={style.publishingRow}>
            <div className={style.publishingGroup}>
              <label className={style.publishingLabel}>
                Access Type <span style={{ color: "var(--app-primary)" }}>*</span>
              </label>
              <div className={style.publishingAccessOptions}>
                {ACCESS_OPTIONS.map(({ value, label }) => {
                  const checked =
                    value === "subscription" ? hasSubscription : accessTypes.includes(value);
                  const disabled =
                    lockAccess || (value !== "subscription" && hasSubscription);

                  return (
                    <label
                      key={value}
                      className={style.publishingAccessOption}
                      style={disabled ? { opacity: 0.45, cursor: "not-allowed" } : undefined}
                    >
                      <input
                        type="checkbox"
                        className={style.publishingAccessCheckbox}
                        checked={checked}
                        disabled={disabled}
                        onChange={() => handleAccessToggle(value)}
                      />
                      {label}
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {showPaidPricing && showAccessControls && isPaid && (
          <div className={style.publishingRow}>
            {showRentFields && (
              <>
                <div className={style.publishingGroup}>
                  <label className={style.publishingLabel}>
                    Rent Price <span style={{ color: "var(--app-primary)" }}>*</span>
                  </label>
                  <input
                    type="number"
                    name="rent_price"
                    className={style.publishingInput}
                    placeholder="0.00"
                    value={publishingData?.rent_price ?? ""}
                    onChange={handleChange}
                    disabled={lockAccess}
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className={style.publishingGroup}>
                  <label className={style.publishingLabel}>
                    Rent Duration (days) <span style={{ color: "var(--app-primary)" }}>*</span>
                  </label>
                  <input
                    type="number"
                    name="rent_duration_days"
                    className={style.publishingInput}
                    placeholder="2"
                    value={publishingData?.rent_duration_days ?? ""}
                    onChange={handleChange}
                    disabled={lockAccess}
                    min="1"
                    step="1"
                  />
                </div>
              </>
            )}
            {showBuyFields && (
              <div className={style.publishingGroup}>
                <label className={style.publishingLabel}>
                  Buy Price <span style={{ color: "var(--app-primary)" }}>*</span>
                </label>
                <input
                  type="number"
                  name="buy_price"
                  className={style.publishingInput}
                  placeholder="0.00"
                  value={publishingData?.buy_price ?? ""}
                  onChange={handleChange}
                  disabled={lockAccess}
                  min="0"
                  step="0.01"
                />
              </div>
            )}
            <div className={style.publishingGroup}>
              <label className={style.publishingLabel}>
                Currency <span style={{ color: "var(--app-primary)" }}>*</span>
              </label>
              <select
                name="currency"
                className={style.publishingSelect}
                value={publishingData?.currency || ""}
                onChange={handleChange}
                disabled={lockAccess}
              >
                <option value="">Select</option>
                {CURRENCY_OPTIONS.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* ── Row 3b: Preferred Orientation + Autoplay Enabled — series only ── */}
        {/* {isSeries && (
          <div className={style.publishingRow}>
            {showRentFields && (
              <>
                <div className={style.publishingGroup}>
                  <label className={style.publishingLabel}>
                    Rent Price <span style={{ color: "var(--app-primary)" }}>*</span>
                  </label>
                  <input
                    type="number"
                    name="rent_price"
                    className={style.publishingInput}
                    placeholder="0.00"
                    value={publishingData?.rent_price ?? ""}
                    onChange={handleChange}
                    disabled={lockAccess}
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className={style.publishingGroup}>
                  <label className={style.publishingLabel}>
                    Rent Duration (days) <span style={{ color: "var(--app-primary)" }}>*</span>
                  </label>
                  <input
                    type="number"
                    name="rent_duration_days"
                    className={style.publishingInput}
                    placeholder="2"
                    value={publishingData?.rent_duration_days ?? ""}
                    onChange={handleChange}
                    disabled={lockAccess}
                    min="1"
                    step="1"
                  />
                </div>
              </>
            )}
            {showBuyFields && (
              <div className={style.publishingGroup}>
                <label className={style.publishingLabel}>
                  Buy Price <span style={{ color: "var(--app-primary)" }}>*</span>
                </label>
                <input
                  type="number"
                  name="buy_price"
                  className={style.publishingInput}
                  placeholder="0.00"
                  value={publishingData?.buy_price ?? ""}
                  onChange={handleChange}
                  disabled={lockAccess}
                  min="0"
                  step="0.01"
                />
              </div>
            )}
            <div className={style.publishingGroup}>
              <label className={style.publishingLabel}>Orientation</label>
              <select
                name="preferred_orientation"
                className={style.publishingSelect}
                value={publishingData?.preferred_orientation || ""}
                onChange={handleChange}
                disabled={lockAccess}
              >
                <option value="">Select</option>
                <option value="landscape">Landscape</option>
                <option value="portrait">portrait</option>
              </select>
            </div>
            <div className={style.publishingGroup}>
              <label className={style.publishingLabel}>Autoplay </label>
              <select
                name="autoplay_enabled"
                className={style.publishingSelect}
                value={publishingData?.autoplay_enabled ?? ""}
                onChange={handleChange}
              >
                <option value="">Select</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </div>
          </div>
        )} */}

        {/* ── Row 4: Publish Date (when applicable) ── */}
        {(!isField || Episodes || isEpisodeAdd) && (
          <div className={style.publishingRow}>
            <div className={style.publishingGroup}>
              <label className={style.publishingLabel}>Publish Date</label>
              <input
                type="date"
                name="publishing_date"
                className={style.publishingInput}
                value={publishingData?.publishing_date || ""}
                onChange={handleChange}
                max="9999-12-31"
              />
            </div>
          </div>
        )}

        {/* ── Remaining Rows: Hidden if isField is true OR if it's the Episode flow ── */}
        {!isField && !isEpisodeAdd && Episodes && (
          <>
            {/* ── Row 5: Content Tier + PPV Price + License Expiry ── */}
            <div className={style.publishingThreeRow}>
              <div className={style.publishingGroup}>
                <label className={style.publishingLabel}>Content Tier</label>
                <select
                  name="contentTier"
                  className={style.publishingSelect}
                  value={publishingData?.contentTier || ""}
                  onChange={handleChange}
                >
                  <option value="">Select</option>
                  {CONTENT_TIER_OPTIONS?.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </div>
              <div className={style.publishingGroup}>
                <label className={style.publishingLabel}>PPV Price ($)</label>
                <select
                  name="ppvPrice"
                  className={style.publishingSelect}
                  value={publishingData?.ppvPrice || ""}
                  onChange={handleChange}
                >
                  <option value="">Select</option>
                  {["1.99", "2.99", "4.99", "9.99", "14.99", "19.99"].map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </div>
              <div className={style.publishingGroup}>
                <label className={style.publishingLabel}>License Expiry</label>
                <input
                  type="date"
                  name="licenseExpiry"
                  className={style.publishingInput}
                  value={publishingData?.licenseExpiry || ""}
                  onChange={handleChange}
                  max="9999-12-31"
                />
              </div>
            </div>

            {/* ── Row 6: Featured Placement + Featured Until ── */}
            <div className={style.publishingRow}>
              <div className={style.publishingGroup}>
                <label className={style.publishingLabel}>Featured Placement</label>
                <select
                  name="featuredPlacement"
                  className={style.publishingSelect}
                  value={publishingData?.featuredPlacement || "Not Featured"}
                  onChange={handleChange}
                >
                  {FEATURED_PLACEMENT_OPTIONS?.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </div>
              <div className={style.publishingGroup}>
                <label className={style.publishingLabel}>Featured Until</label>
                <input
                  type="date"
                  name="featuredUntil"
                  className={style.publishingInput}
                  value={publishingData?.featuredUntil || ""}
                  onChange={handleChange}
                  max="9999-12-31"
                />
              </div>
            </div>
          </>
        )}

      </div>
    </Accordion>
  );
};

export default Publishing;